import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { repositoryClassificationsApi } from '../classificationsApi';
import { repositoryApi } from '../repositoryApi';
import type {
  RepositoryUploadDefaults,
  RepositoryClassificationOption,
} from '../repositoryTypes';
import { UploadDock } from './UploadDock';

/**
 * PDF only, 50 MB, matching `FileCategoryRegistry`'s repository-documents category.
 *
 * A courtesy, not a control: the server validates the extension, the declared type, the magic
 * bytes and the size again, because anything decided in a browser is decided by whoever is
 * holding the browser.
 */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export interface QueuedUpload {
  id: string;
  file: File;

  /** Editable. Seeded from the file with `.pdf` stripped — see `displayNameOf`. */
  name: string;

  size: number;
  folderId: string;
  folderName: string;

  /** Whether the destination folder issues reference numbers. Decides the classification rule. */
  isControlled: boolean;

  /*
    There is no department on a row. It is the uploader's own, resolved server-side with SYS as
    the floor — product call, 18 Sep 2026 — so the queue neither asks for it nor sends it.
  */

  classificationId: string | null;

  status: UploadStatus;
  progress: number;
  error: string;

  /** A live sibling already holds this name. A WARNING — the server suffixes rather than refusing. */
  isDuplicate: boolean;
}

export type UploadOrder = 'simultaneous' | 'sequential';

export interface UploadBatch {
  id: string;
  label: string;
  order: UploadOrder;
  items: QueuedUpload[];
}

interface RepositoryUploadValue {
  batches: UploadBatch[];
  activeBatchId: string | null;
  open: boolean;
  minimized: boolean;
  /** Everything not yet uploaded — the count on the minimized pill. */
  outstanding: number;

  classifications: RepositoryClassificationOption[];

  /**
   * The business unit every upload in this session is filed under. For DISPLAY only.
   *
   * Null until the resolve lands, or when it failed — the footer simply does not name a unit
   * rather than guessing one.
   */
  filedUnder: RepositoryUploadDefaults | null;

  enqueue: (
    files: FileList | File[],
    destination: {
      folderId: string;
      folderName: string;
      isControlled: boolean;
      existingNames: string[];
    },
  ) => void;

  setActiveBatchId: (id: string) => void;
  setMinimized: (value: boolean) => void;
  setOrder: (batchId: string, order: UploadOrder) => void;

  updateItem: (batchId: string, itemId: string, patch: Partial<QueuedUpload>) => void;
  moveItem: (batchId: string, itemId: string, direction: 'up' | 'down') => void;
  removeItem: (batchId: string, itemId: string) => void;

  uploadOne: (batchId: string, itemId: string) => Promise<void>;
  uploadAll: (batchId: string) => Promise<void>;

  closeBatch: (batchId: string) => void;
  clearAll: () => void;
}

const RepositoryUploadContext = createContext<RepositoryUploadValue | null>(null);

/**
 * Strips a trailing `.pdf` for display.
 *
 * The reader names the DOCUMENT, and the extension is a property of the file rather than of
 * what it is called — `Increase-in-Contract-Tapaz`, not `Increase-in-Contract-Tapaz.pdf`. The
 * original file name is kept on the stored-file row regardless.
 */
export function displayNameOf(fileName: string): string {
  const trimmed = fileName.trim();

  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed.slice(0, -4) : trimmed;
}

let batchCounter = 0;

/**
 * The repository's upload queue.
 *
 * **A DOCK, not a modal**, and mounted at app level rather than on the page — which is the
 * whole reason it is a provider. An upload of thirty megabytes outlives the reader's interest
 * in watching it: they minimize the queue, carry on browsing, and it keeps going. A modal on
 * the index page would die the moment they navigated, taking the batch with it.
 *
 * **Every behaviour here is the GRC Documents workspace's**, which is what that prototype gets
 * right — people use a repository like a file browser, and a file browser's upload is a queue
 * with per-file control rather than a form. What is rsys_'s is how it is BUILT: the shared
 * inputs, the tokens, the table treatment.
 *
 * The one thing deliberately NOT carried across is GRC's confidential-in-a-public-folder
 * warning. A repository folder here has no `IsPublic` column, so there is nothing to check —
 * building the warning would mean inventing the concept, and a control that cannot be true is
 * worse than an absent one.
 */
export function RepositoryUploadProvider({ children }: { children: ReactNode }) {
  const { user, hasPermission } = useAuth();

  const [batches, setBatches] = useState<UploadBatch[]>([]);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);

  /*
    The queue as it stands RIGHT NOW, for the upload functions to read.

    They cannot close over `batches`: a simultaneous batch has several uploads in flight and
    each one would see the snapshot from the render that started it. Reading through a setState
    updater would work and is worse — calling a setter purely to read is a hack StrictMode is
    free to invoke twice.
  */
  const batchesRef = useRef<UploadBatch[]>([]);

  useEffect(() => {
    batchesRef.current = batches;
  }, [batches]);

  const [classifications, setClassifications] = useState<RepositoryClassificationOption[]>([]);

  /**
   * The business unit every file in this session is filed under, for DISPLAY only.
   *
   * **Product call, 18 Sep 2026: the department is not asked for.** It is the uploader's own,
   * resolved server-side from their record, with the reserved `SYS` code as the floor — so
   * there is no picker, no column, and no field on the request. What the queue does with this
   * is state it once in the footer, because a permanent reference number is about to carry it.
   */
  const [filedUnder, setFiledUnder] = useState<RepositoryUploadDefaults | null>(null);

  /*
    Fetched once the reader is signed in AND may create — both feeds belong to an upload, so
    fetching them for an account that can never upload is two requests for nothing.

    A failure leaves the dock working rather than broken: the classification picker is empty
    and a coded folder's requirement is still enforced server side, and the footer simply does
    not name a unit.
  */
  useEffect(() => {
    if (!user || !hasPermission('repository.create')) {
      setClassifications([]);
      setFiledUnder(null);

      return;
    }

    const controller = new AbortController();

    Promise.all([
      repositoryClassificationsApi.options(controller.signal),
      repositoryApi.uploadDefaults(controller.signal),
    ])
      .then(([tiers, defaults]) => {
        setClassifications(tiers);
        setFiledUnder(defaults);
      })
      .catch(() => {
        // Silent: an upload has not been attempted yet, so there is nothing on screen to
        // attach a failure to. The row's own refusal is where a reader finds out.
      });

    return () => controller.abort();
  }, [user, hasPermission]);

  const patchItem = useCallback(
    (batchId: string, itemId: string, patch: Partial<QueuedUpload>) => {
      setBatches((current) =>
        current.map((batch) =>
          batch.id !== batchId
            ? batch
            : {
                ...batch,
                items: batch.items.map((item) =>
                  item.id === itemId ? { ...item, ...patch } : item,
                ),
              },
        ),
      );
    },
    [],
  );

  const enqueue = useCallback<RepositoryUploadValue['enqueue']>(
    (files, destination) => {
      const all = Array.from(files);
      const pdfs = all.filter((file) => file.name.toLowerCase().endsWith('.pdf'));
      const accepted = pdfs.filter((file) => file.size <= MAX_UPLOAD_BYTES);

      /*
        Rejected files are dropped HERE rather than queued and refused later, and the counts
        are reported on the batch so the reader can see what did not make it. A row that can
        never succeed is not a row — it is a queue that lies about its own length.
      */
      if (accepted.length === 0) {
        return;
      }

      const taken = new Set(destination.existingNames.map((name) => name.toLowerCase().trim()));

      batchCounter += 1;

      const batch: UploadBatch = {
        id: `batch-${Date.now()}-${batchCounter}`,
        label: `Batch ${batchCounter}`,
        order: 'simultaneous',
        items: accepted.map((file, index) => {
          const name = displayNameOf(file.name);

          return {
            id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
            file,
            name,
            size: file.size,
            folderId: destination.folderId,
            folderName: destination.folderName,
            isControlled: destination.isControlled,
            /*
              The matrix's least-restricted value leads, so the first option is the safe
              default — and only inside a coded folder, where one is required. Pre-selecting a
              classification on a plain file would record an answer nobody gave.
            */
            classificationId: destination.isControlled
              ? (classifications[0]?.id ?? null)
              : null,

            status: 'pending' as const,
            progress: 0,
            error: '',
            isDuplicate: taken.has(name.toLowerCase()),
          };
        }),
      };

      setBatches((current) => [...current, batch]);
      setActiveBatchId(batch.id);
      setMinimized(false);
    },
    [classifications],
  );

  /**
   * Uploads one row, reporting progress onto it.
   *
   * Reads the row out of the CURRENT state rather than closing over it, because a batch
   * uploading simultaneously has several of these in flight and each one's `setBatches` would
   * otherwise overwrite the others' progress with a stale snapshot.
   */
  const uploadOne = useCallback(
    async (batchId: string, itemId: string) => {
      const item = batchesRef.current
        .find((b) => b.id === batchId)
        ?.items.find((i) => i.id === itemId);

      if (!item || item.status === 'uploading' || item.status === 'success') {
        return;
      }

      if (!item.name.trim()) {
        patchItem(batchId, itemId, { status: 'error', error: 'A file name is required.' });

        return;
      }

      if (item.isControlled && !item.classificationId) {
        patchItem(batchId, itemId, {
          status: 'error',
          error: 'Choose a classification — this folder holds controlled documents.',
        });

        return;
      }

      patchItem(batchId, itemId, { status: 'uploading', progress: 0, error: '' });

      try {
        await repositoryApi.upload(
          item.file,
          {
            folderId: item.folderId,
            name: item.name.trim(),
            classificationId: item.classificationId,
          },
          { onProgress: (progress) => patchItem(batchId, itemId, { progress }) },
        );

        patchItem(batchId, itemId, { status: 'success', progress: 100, error: '' });

        /*
          A window event rather than a callback, because the dock is mounted at app level and
          the listing that wants refreshing is wherever the reader happens to be. GRC does the
          same, and it is the only thing that survives the queue outliving its page.
        */
        window.dispatchEvent(new CustomEvent('repository:uploaded'));
      } catch (caught) {
        patchItem(batchId, itemId, {
          status: 'error',
          error:
            caught instanceof ApiError ? caught.message : 'That file could not be uploaded.',
        });
      }
    },
    [patchItem],
  );

  const uploadAll = useCallback(
    async (batchId: string) => {
      const batch = batchesRef.current.find((b) => b.id === batchId);

      if (!batch) {
        return;
      }

      const pending = batch.items.filter((i) => i.status === 'pending' || i.status === 'error');

      if (batch.order === 'sequential') {
        // One after another, in the listed order — which is what the reorder arrows are for.
        for (const item of pending) {
          await uploadOne(batchId, item.id);
        }

        return;
      }

      await Promise.all(pending.map((item) => uploadOne(batchId, item.id)));
    },
    [uploadOne],
  );

  const value = useMemo<RepositoryUploadValue>(
    () => ({
      batches,
      activeBatchId,
      open: batches.length > 0,
      minimized,
      outstanding: batches
        .flatMap((b) => b.items)
        .filter((i) => i.status !== 'success').length,
      filedUnder,
      classifications,
      enqueue,
      setActiveBatchId,
      setMinimized,
      setOrder: (batchId, order) =>
        setBatches((current) =>
          current.map((b) => (b.id === batchId ? { ...b, order } : b)),
        ),
      updateItem: patchItem,

      /*
        There is no "apply department to the batch" either — the reference's helper for
        pushing one row's department onto its siblings. With no per-row department there is
        nothing to push.
      */

      moveItem: (batchId, itemId, direction) =>
        setBatches((current) =>
          current.map((batch) => {
            if (batch.id !== batchId) return batch;

            const items = [...batch.items];
            const index = items.findIndex((i) => i.id === itemId);
            const swap = direction === 'up' ? index - 1 : index + 1;

            if (index < 0 || swap < 0 || swap >= items.length) return batch;

            [items[index], items[swap]] = [items[swap], items[index]];

            return { ...batch, items };
          }),
        ),
      removeItem: (batchId, itemId) =>
        setBatches((current) =>
          current.map((batch) =>
            batch.id !== batchId
              ? batch
              : { ...batch, items: batch.items.filter((i) => i.id !== itemId) },
          ),
        ),
      uploadOne,
      uploadAll,
      closeBatch: (batchId) =>
        setBatches((current) => {
          const next = current.filter((b) => b.id !== batchId);

          if (activeBatchId === batchId) {
            setActiveBatchId(next.length ? next[next.length - 1].id : null);
          }

          return next;
        }),
      clearAll: () => {
        setBatches([]);
        setActiveBatchId(null);
        setMinimized(false);
      },
    }),
    [
      batches,
      activeBatchId,
      minimized,
      filedUnder,
      classifications,
      enqueue,
      patchItem,
      uploadOne,
      uploadAll,
    ],
  );

  return (
    <RepositoryUploadContext.Provider value={value}>
      {children}
      <UploadDock />
    </RepositoryUploadContext.Provider>
  );
}

export function useRepositoryUpload(): RepositoryUploadValue {
  const context = useContext(RepositoryUploadContext);

  if (!context) {
    throw new Error('useRepositoryUpload must be used inside a <RepositoryUploadProvider>.');
  }

  return context;
}
